import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Building2, ClipboardList, Clock, CheckCircle, XCircle, DollarSign, TrendingUp, ArrowUpRight, RefreshCcw } from "lucide-react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";

const StatCard = ({ icon: Icon, label, value, color = "text-gold", to }) => {
    const inner = (
        <div className="border border-border_green bg-emerald_surface p-5 hover:border-gold transition-colors h-full">
            <div className="flex items-center justify-between">
                <Icon className={`w-5 h-5 ${color}`} />
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted_green">{label}</span>
            </div>
            <div className="mt-5 font-serif text-3xl">{value}</div>
        </div>
    );
    return to ? <Link to={to}>{inner}</Link> : inner;
};

const AdminDashboard = () => {
    const { t, rates, loadRates } = useApp();
    const [stats, setStats] = useState({});
    const [reservations, setReservations] = useState([]);
    const [occupancy, setOccupancy] = useState([]);

    const load = async () => {
        try {
            const [s, r, o] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/reservations"),
                api.get("/admin/occupancy"),
            ]);
            setStats(s.data);
            setReservations(r.data);
            setOccupancy(o.data);
        } catch { /* dashboard load failed; empty state shown */ }
    };

    useEffect(() => { load(); }, []);

    const recent = reservations.slice(0, 6);

    const topHotels = useMemo(() => {
        const map = {};
        for (const r of reservations) {
            if (!map[r.hotel_name]) map[r.hotel_name] = { name: r.hotel_name, count: 0, revenue: 0 };
            map[r.hotel_name].count += 1;
            if (r.status === "approved") map[r.hotel_name].revenue += r.total_price || 0;
        }
        return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 5);
    }, [reservations]);

    const overallOcc = useMemo(() => {
        if (!occupancy.length) return 0;
        const inv = occupancy.reduce((s, h) => s + (h.total_inventory || 0), 0);
        const bk = occupancy.reduce((s, h) => s + (h.total_booked || 0), 0);
        return inv ? Math.round((bk / inv) * 100) : 0;
    }, [occupancy]);

    const lastUpdated = rates.fetched_at ? new Date(rates.fetched_at).toLocaleTimeString("tr-TR") : "—";

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <h1 className="font-serif text-4xl mb-2">{t("admin.dashboard")}</h1>
                    <p className="text-muted_green">Genel bakış · {new Date().toLocaleDateString("tr-TR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
                </div>
                <Link to="/admin/reservations" className="btn-outline-gold !text-xs">
                    Rezervasyonları Yönet <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
            </div>

            {/* Top stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard icon={Building2} label="Otel" value={stats.total_hotels || 0} to="/admin/hotels" />
                <StatCard icon={Clock} label="Bekleyen" value={stats.pending || 0} color="text-yellow-400" to="/admin/reservations" />
                <StatCard icon={CheckCircle} label="Onaylanan" value={stats.approved || 0} color="text-emerald-400" to="/admin/reservations" />
                <StatCard icon={TrendingUp} label="Doluluk" value={`%${overallOcc}`} to="/admin/occupancy" />
            </div>

            {/* Revenue + Rates row */}
            <div className="grid lg:grid-cols-3 gap-4 mb-6">
                <div className="lg:col-span-2 border border-border_green bg-emerald_surface p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-gold">Toplam Gelir</div>
                            <div className="text-xs text-muted_green">Onaylanan rezervasyonlar</div>
                        </div>
                        <DollarSign className="w-5 h-5 text-gold" />
                    </div>
                    <div className="font-serif text-5xl text-gold">{(stats.revenue || 0).toLocaleString("tr-TR")} <span className="text-2xl">SAR</span></div>
                    {rates?.rates && (
                        <div className="mt-3 text-xs text-muted_green flex items-center gap-4">
                            <span>≈ {((stats.revenue || 0) * rates.rates.TRY).toLocaleString("tr-TR", { maximumFractionDigits: 0 })} ₺</span>
                            <span>≈ ${((stats.revenue || 0) * rates.rates.USD).toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
                        </div>
                    )}
                </div>
                <div className="border border-border_green bg-emerald_surface p-6">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-gold">Canlı Kur</div>
                        <button onClick={loadRates} className="text-muted_green hover:text-gold" title="Yenile">
                            <RefreshCcw className="w-3.5 h-3.5" />
                        </button>
                    </div>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted_green">1 SAR</span><span className="text-gold font-mono">{rates?.rates?.TRY?.toFixed(4) || "—"} ₺</span></div>
                        <div className="flex justify-between"><span className="text-muted_green">1 SAR</span><span className="text-gold font-mono">${rates?.rates?.USD?.toFixed(4) || "—"}</span></div>
                        <div className="flex justify-between"><span className="text-muted_green">USD/TRY</span><span className="font-mono">{rates?.raw?.USD_TRY?.toFixed(4) || "—"}</span></div>
                    </div>
                    <div className="text-[10px] text-muted_green mt-3 pt-3 border-t border-border_green">
                        Kaynak: {rates?.source || "—"} · {lastUpdated}
                    </div>
                </div>
            </div>

            {/* Reservations + Top hotels */}
            <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 border border-border_green bg-emerald_surface">
                    <div className="px-6 py-4 border-b border-border_green flex items-center justify-between">
                        <h2 className="font-serif text-xl">Son Rezervasyonlar</h2>
                        <Link to="/admin/reservations" className="text-xs text-gold hover:underline inline-flex items-center gap-1">
                            Tümü <ArrowUpRight className="w-3 h-3" />
                        </Link>
                    </div>
                    <div className="divide-y divide-border_green">
                        {recent.length === 0 && <div className="p-8 text-center text-muted_green text-sm">Henüz rezervasyon yok</div>}
                        {recent.map((r) => (
                            <div key={r.id} className="px-6 py-3 flex items-center justify-between gap-3">
                                <div className="font-mono text-[11px] text-gold w-24 truncate">{r.reservation_code}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium truncate">{r.customer_name}</div>
                                    <div className="text-xs text-muted_green truncate">{r.hotel_name}</div>
                                </div>
                                <div className="text-xs text-muted_green hidden md:block">{r.check_in}</div>
                                <div className="text-gold font-serif text-lg w-24 text-right">{r.total_price} SAR</div>
                                <div className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border w-24 text-center ${
                                    r.status === "pending" ? "text-yellow-400 border-yellow-400/40" :
                                    r.status === "approved" ? "text-emerald-400 border-emerald-400/40" :
                                    "text-red-400 border-red-400/40"
                                }`}>{r.status === "pending" ? "Beklemede" : r.status === "approved" ? "Onaylı" : "Reddedildi"}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="border border-border_green bg-emerald_surface">
                    <div className="px-6 py-4 border-b border-border_green">
                        <h2 className="font-serif text-xl">En Çok Tercih Edilen</h2>
                    </div>
                    <div className="divide-y divide-border_green">
                        {topHotels.length === 0 && <div className="p-8 text-center text-muted_green text-sm">Veri yok</div>}
                        {topHotels.map((h, i) => (
                            <div key={i} className="px-6 py-3">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm truncate">{h.name}</div>
                                    <div className="text-xs text-gold font-mono">{h.count}</div>
                                </div>
                                {h.revenue > 0 && (
                                    <div className="text-[10px] text-muted_green mt-0.5">{h.revenue.toLocaleString()} SAR gelir</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
